/**
 * Replay = seed + stream di input compresso. LA FEATURE FIRMA.
 *
 * Un ghost NON è una lista di posizioni: è la sequenza dei momenti in cui il dito
 * preme/rilascia. Ri-simulando lo stesso motore con lo stesso seed si riottiene la
 * traiettoria esatta (pochi KB, N ghost al costo di zero rete). Ed è ciò che, in
 * Sprint 3, permette al server di RI-SIMULARE la run e validare il tempo
 * (anti-cheat più forte di qualsiasi euristica). Vedi docs §4.1.
 *
 * Formato: `toggles` = tempi (s) in cui l'input cambia stato, si parte da RILASCIATO.
 * held(t) = (numero di toggle <= t) è dispari.
 */

import type { Terrain } from './terrain';
import { initSim, step, type SimState } from './sim';
import * as C from './constants';
import { q } from './dmath';
import { hashSeed } from './rng';

export interface Replay {
  seed: string;
  toggles: number[]; // tempi di sim (s), crescenti; parte da released
  timeMs: number; // tempo finale al traguardo (ms), per confronto rapido
}

/** Registra i toggle di input durante il gioco live. */
export class InputRecorder {
  private readonly toggles: number[] = [];
  private held = false;

  set(down: boolean, simTime: number): void {
    if (down === this.held) return;
    this.held = down;
    this.toggles.push(simTime);
  }

  isHeld(): boolean {
    return this.held;
  }

  build(seed: string, timeMs: number): Replay {
    return { seed, toggles: this.toggles.slice(), timeMs };
  }
}

/** Controller di playback: dato un replay, dice se il dito è premuto a un dato step. */
export class ReplayController {
  private idx = 0;
  private held = false;
  constructor(private readonly toggles: readonly number[]) {}

  heldAt(t: number): boolean {
    while (this.idx < this.toggles.length && this.toggles[this.idx]! <= t) {
      this.held = !this.held;
      this.idx++;
    }
    return this.held;
  }

  reset(): void {
    this.idx = 0;
    this.held = false;
  }
}

/** Ri-simula un replay dall'inizio alla fine e restituisce lo stato finale. */
export function runReplay(terrain: Terrain, replay: Replay): SimState {
  const s = initSim(terrain);
  const ctl = new ReplayController(replay.toggles);
  while (!s.finished && s.steps < C.MAX_STEPS) {
    step(s, terrain, ctl.heldAt(s.t));
  }
  return s;
}

/** Campiona un replay in una traiettoria (t,x,y) per disegnare il ghost. */
export interface GhostFrame {
  t: number;
  x: number;
  y: number;
}
export function sampleGhost(terrain: Terrain, replay: Replay, everyN = 2): GhostFrame[] {
  const s = initSim(terrain);
  const ctl = new ReplayController(replay.toggles);
  const frames: GhostFrame[] = [{ t: s.t, x: s.x, y: s.y }];
  let k = 0;
  while (!s.finished && s.steps < C.MAX_STEPS) {
    step(s, terrain, ctl.heldAt(s.t));
    if (++k % everyN === 0) frames.push({ t: s.t, x: s.x, y: s.y });
  }
  frames.push({ t: s.t, x: s.x, y: s.y });
  return frames;
}

/**
 * Hash dello stato finale — per i golden-run test.
 * Quantizza i campi significativi così due run "identiche" collassano allo stesso
 * valore anche col rumore dell'ultimo bit. Se questo hash cambia tra due motori,
 * il determinismo è rotto.
 */
export function stateHash(s: SimState): number {
  const sig = [q(s.x), q(s.y), q(s.vx), q(s.vy), q(s.t, 4), s.steps, s.finished ? 1 : 0].join('|');
  return hashSeed(sig);
}
